import { PhoneInput } from './PhoneInput';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { contactApi } from '../services/api';
import { SupportContext, supportContextFromLocation } from '../utils/supportContext';
import { ETAPPIO_COMPANY } from '../constants/company';

export function ContactForm({ context }: { context?: SupportContext }) {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'saved' | 'error'>('idle');
  const [sending, setSending] = useState(false);
  const [savedId, setSavedId] = useState('');
  const role = user?.isAdmin ? 'admin' : user?.isSupport ? 'support' : user?.personaType;
  const supportContext = context ?? supportContextFromLocation(location);
  const locked = sending || isLoading || Boolean(user?.impersonation);
  const change = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(current => ({ ...current, [event.target.name]: event.target.value }));
    setStatus('idle');
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (locked) return;
    setSending(true);
    setStatus('idle');
    try {
      const response = user
        ? await contactApi.sendSupportMessage({ message: form.message.trim(), context: supportContext })
        : await contactApi.sendMessage({ ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), message: form.message.trim() });
      const delivery = response.data.emailDeliveryStatus;
      setStatus(user && delivery !== 'sent' ? 'saved' : 'success');
      setSavedId(response.data.id);
      setForm(current => ({ ...current, message: '' }));
    } catch {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={submit}>
      <fieldset disabled={locked}>
        {user && <p className="text-muted">{t('support.prefilled')}</p>}
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="contact-name">{t('support.name')}</label>
            <input id="contact-name" name="name" autoComplete="name" className="form-control" value={user?.fullName ?? form.name} onChange={change} readOnly={Boolean(user)} required maxLength={255} />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="contact-email">{t('support.email')}</label>
            <input id="contact-email" name="email" type="email" autoComplete="email" className="form-control" value={user?.email ?? form.email} onChange={change} readOnly={Boolean(user)} required maxLength={320} />
          </div>
          {user ? (
            <div className="col-12">
              <label className="form-label" htmlFor="contact-role">{t('support.role')}</label>
              <input id="contact-role" className="form-control" value={t(`persona.${role}`)} readOnly />
            </div>
          ) : (
            <div className="col-12">
              <label className="form-label" htmlFor="contact-phone">{t('support.phoneOptional')}</label>
              <PhoneInput id="contact-phone" name="phone" type="tel" autoComplete="tel" className="form-control" value={form.phone} onChange={change} maxLength={50} />
            </div>
          )}
          {user && (
            <div className="col-12">
              <div className="bg-light border rounded p-3 text-break" data-testid="support-context">
                <h3 className="h6">{t('support.contextTitle')}</h3>
                <p className="small text-muted">{t('support.contextHelp')}</p>
                <dl className="mb-0">
                  <dt>{t('support.page')}</dt><dd>{supportContext.sourcePath}</dd>
                  {supportContext.projectId && <><dt>{t('support.project')}</dt><dd>{supportContext.projectId}</dd></>}
                  {supportContext.contractId && <><dt>{t('support.contract')}</dt><dd>{supportContext.contractId}</dd></>}
                </dl>
              </div>
            </div>
          )}
          <div className="col-12">
            <label className="form-label" htmlFor="contact-message">{t('support.message')}</label>
            <textarea id="contact-message" name="message" className="form-control" value={form.message} onChange={change} required minLength={5} maxLength={10000} rows={6} />
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary w-100">{t(sending ? 'support.sending' : 'support.submit')}</button>
          </div>
        </div>
      </fieldset>
      {status === 'success' && <p className="alert alert-success mt-3" role="status">{t('support.success')}</p>}
      {status === 'saved' && <div className="alert alert-warning mt-3" role="status">
        <p>{t('support.saved', { id: savedId })}</p>
        <a className="contact-touch-link" href={`mailto:${ETAPPIO_COMPANY.email}`}>{t('contactPage.emailCta')}</a>
      </div>}
      {status === 'error' && <p className="alert alert-danger mt-3" role="alert">{t('support.error')}</p>}
    </form>
  );
}
